// ─────────────────────────────────────────────────────────────────
// Status för ett köp eller en hyra i klartext (Min garderob: Köpta, Hyrda,
// Spårning, Att skicka, Försäljning). Bara beräkningar här — texten finns i
// i18n (orderStatus.*) och ritas av OrderStatusPanel i App.tsx.
//
// Statusarna på wardrobe_transactions (api/src/resolvers.ts, payments/,
// returns/returns.ts): awaiting_payment → pending (betald) → shipped →
// delivered → [return_requested → return_shipped] → completed, samt
// cancelled och refunded. Hämtning hoppar över shipped. Ovanpå det:
// problemReportedAt (köparskyddet) och buyerCancelStatus (hämtning).
//
// Hyra i dag: itemPrice = pris per månad, månad 1 betalas i kassan och
// månad 2–N blir rentalCharges när plagget levererats (api/src/rentals/billing.ts).
// S1 (backlog.md): hela perioden betalas i kassan. Då finns inga månadsdragningar
// kvar och rentalPrepaid sätts — panelen visar "Betalt: hela perioden".
// ─────────────────────────────────────────────────────────────────

import type { RentalCharge } from '../api';

export type OrderRole = 'buyer' | 'seller';

export type OrderPhase =
  | 'awaiting_payment'
  | 'paid_to_ship'      // betald, säljaren/uthyraren ska skicka
  | 'paid_pickup'       // betald, ska hämtas
  | 'cancel_requested'  // hämtning: köparen vill avbryta
  | 'shipped'
  | 'delivered'         // köp levererat
  | 'renting'           // hyra pågår
  | 'return_requested'
  | 'return_shipped'
  | 'returned'          // hyra återlämnad / köp returnerat
  | 'completed'
  | 'cancelled'
  | 'refunded'
  | 'problem'
  | 'unknown';

export type StepKey = 'paid' | 'shipped' | 'pickedUp' | 'delivered' | 'renting' | 'returned' | 'returnRequested' | 'returnShipped' | 'returnReceived';
export type StepState = 'done' | 'current' | 'todo';

export interface OrderTx {
  type: string;
  status: string;
  deliveryMethod: string;
  bindingMonths?: number | null;
  itemPrice: number;
  shippingCost: number;
  shippingProvider?: string | null;
  protectionFee: number;
  commissionAmount: number;
  sellerPayout: number;
  payoutStatus?: string | null;
  deliveredAt?: string | null;
  buyerHandoverConfirmedAt?: string | null;
  returnRequestedAt?: string | null;
  returnReceivedAt?: string | null;
  problemReportedAt?: string | null;
  buyerCancelStatus?: string | null;
  rentalCharges?: RentalCharge[] | null;
  /** S1: hela hyresperioden betald i kassan (fältet finns inte i API:t än). */
  rentalPrepaid?: boolean | null;
}

const CLOSED = new Set(['completed', 'cancelled', 'refunded']);

export function isRent(tx: Pick<OrderTx, 'type'>): boolean {
  return tx.type === 'rent';
}

export function isPickup(tx: Pick<OrderTx, 'deliveryMethod'>): boolean {
  return tx.deliveryMethod === 'pickup';
}

/** Avslutad (visas inte som pågående). */
export function isClosed(tx: Pick<OrderTx, 'status'>): boolean {
  return CLOSED.has(tx.status);
}

export function orderPhase(tx: OrderTx): OrderPhase {
  const s = tx.status;
  if (tx.problemReportedAt && !CLOSED.has(s) && tx.payoutStatus !== 'transferred') return 'problem';
  switch (s) {
    case 'awaiting_payment': return 'awaiting_payment';
    case 'pending':
      if (isPickup(tx) && (tx.buyerCancelStatus === 'requested' || tx.buyerCancelStatus === 'objected')) return 'cancel_requested';
      return isPickup(tx) ? 'paid_pickup' : 'paid_to_ship';
    case 'shipped': return 'shipped';
    case 'delivered': return isRent(tx) ? 'renting' : 'delivered';
    case 'return_requested': return 'return_requested';
    case 'return_shipped': return 'return_shipped';
    case 'return_received': return 'returned';
    case 'completed': return isRent(tx) || tx.returnRequestedAt || tx.returnReceivedAt ? 'returned' : 'completed';
    case 'cancelled': return 'cancelled';
    case 'refunded': return 'refunded';
    default: return 'unknown';
  }
}

/** Stegvisaren. Tom för avbrutna/återbetalda och okända statusar. */
export function orderSteps(tx: OrderTx): { key: StepKey; state: StepState }[] {
  const phase = orderPhase(tx);
  if (phase === 'cancelled' || phase === 'refunded' || phase === 'unknown') return [];
  const pickup = isPickup(tx);
  const rent = isRent(tx);
  const keys: StepKey[] = ['paid', ...(pickup ? ['pickedUp' as const] : ['shipped' as const, 'delivered' as const])];
  if (rent) keys.push('renting', 'returned');
  else if (tx.returnRequestedAt || ['return_requested', 'return_shipped', 'return_received'].includes(tx.status)) keys.push('returnRequested', 'returnShipped', 'returnReceived');

  // Index för det steg man står på (allt före är klart).
  const s = tx.status;
  let current: number;
  if (s === 'awaiting_payment') current = 0;
  else if (s === 'pending') current = 1;
  else if (s === 'shipped') current = 2;
  else if (s === 'delivered') current = rent ? keys.indexOf('renting') : keys.length; // köp: klart
  else if (s === 'return_requested') current = rent ? keys.indexOf('returned') : keys.indexOf('returnShipped');
  else if (s === 'return_shipped') current = rent ? keys.indexOf('returned') : keys.indexOf('returnReceived');
  else current = keys.length; // completed / return_received
  // Hyra pågår är ett pågående steg: "klart" först när plagget är tillbaka.
  return keys.map((key, i) => ({ key, state: i < current ? 'done' : i === current ? 'current' : 'todo' }));
}

/** Samma regel som API:t (rentals/billing.ts, returns/returns.ts): 31 jan + 1 mån → sista feb. */
export function addMonths(ms: number, months: number): number {
  const d = new Date(ms);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() < day) d.setDate(0);
  return d.getTime();
}

export interface RentalPeriod {
  months: number;
  /** Leverans/överlämning — null innan plagget kommit fram. */
  startMs: number | null;
  /** Sista dag att lämna tillbaka (samma som item_returns.due_at). */
  endMs: number | null;
  returnedMs: number | null;
}

export function rentalPeriod(tx: OrderTx): RentalPeriod {
  const months = Math.max(1, Number(tx.bindingMonths ?? 1) || 1);
  const startIso = tx.deliveredAt ?? tx.buyerHandoverConfirmedAt ?? null;
  const reached = !['awaiting_payment', 'pending', 'shipped', 'cancelled', 'refunded'].includes(tx.status);
  const startMs = reached && startIso ? new Date(startIso).getTime() : null;
  return {
    months,
    startMs,
    endMs: startMs != null ? addMonths(startMs, months) : null,
    returnedMs: tx.returnReceivedAt ? new Date(tx.returnReceivedAt).getTime() : null,
  };
}

export interface RentalPayments {
  months: number;
  monthly: number;
  periodTotal: number;
  /** Betalt hittills av hyran (utan frakt). */
  paid: number;
  monthsPaid: number;
  /** Återstår att dra (scheduled + action_required, eller månad 2–N före leverans). */
  remaining: number;
  /** Hela perioden betald — inga dragningar kvar. */
  fullyPaid: boolean;
  /** Månadsdragningarna är inte schemalagda än (startar vid leverans). */
  chargesStartOnDelivery: boolean;
  nextDueMs: number | null;
  actionRequired: RentalCharge | null;
  /** Uthyrarens sida: månader som betalats ut. */
  monthsPaidOut: number;
}

export function rentalPayments(tx: OrderTx): RentalPayments {
  const months = Math.max(1, Number(tx.bindingMonths ?? 1) || 1);
  const monthly = Number(tx.itemPrice) || 0;
  const charges = (tx.rentalCharges ?? []).filter(c => c.status !== 'canceled');
  const unpaidStatus = tx.status === 'awaiting_payment';
  const stopped = tx.status === 'cancelled' || tx.status === 'refunded';

  if (tx.rentalPrepaid) {
    const total = monthly * months;
    return { months, monthly, periodTotal: total, paid: unpaidStatus ? 0 : total, monthsPaid: unpaidStatus ? 0 : months, remaining: 0, fullyPaid: !unpaidStatus, chargesStartOnDelivery: false, nextDueMs: null, actionRequired: null, monthsPaidOut: tx.payoutStatus === 'transferred' ? months : 0 };
  }

  const paidCharges = charges.filter(c => c.status === 'paid');
  const firstPaid = unpaidStatus ? 0 : 1;
  const monthsPaid = firstPaid + paidCharges.length;
  const paid = (firstPaid ? monthly : 0) + paidCharges.reduce((s, c) => s + Number(c.amount), 0);
  const open = charges.filter(c => c.status === 'scheduled' || c.status === 'action_required');
  // Före leverans finns inga rentalCharges än: månad 2–N dras efter leveransen.
  const chargesStartOnDelivery = !stopped && !unpaidStatus && charges.length === 0 && months > 1 && rentalPeriod(tx).startMs == null;
  const remaining = stopped ? 0
    : chargesStartOnDelivery ? monthly * (months - 1)
    : open.reduce((s, c) => s + Number(c.amount), 0);
  const next = open.filter(c => c.status === 'scheduled').sort((a, b) => a.dueAt.localeCompare(b.dueAt))[0];
  return {
    months, monthly,
    periodTotal: monthly * months,
    paid, monthsPaid, remaining,
    fullyPaid: !unpaidStatus && !stopped && remaining === 0 && monthsPaid >= months,
    chargesStartOnDelivery,
    nextDueMs: next ? new Date(next.dueAt).getTime() : null,
    actionRequired: charges.find(c => c.status === 'action_required') ?? null,
    monthsPaidOut: (tx.payoutStatus === 'transferred' ? 1 : 0) + charges.filter(c => c.payoutStatus === 'transferred').length,
  };
}

/** Vad köparen betalade i kassan (pris eller första månaden + frakt + köparskydd). */
export function checkoutTotal(tx: OrderTx): number {
  return Number(tx.itemPrice) + Number(tx.shippingCost ?? 0) + Number(tx.protectionFee ?? 0);
}
